import React, { useEffect, useMemo, useCallback, useState } from 'react';
import ActionButton from '../common/ActionButton';
import TabPanel from '../common/TabPanel';
import { MODAL_ACTION_TYPE } from '../../utils/constants/modal';
import { useDispatch } from 'react-redux';
import { hideAllModals } from 'store/slices/modalSlice';
import {
    CarOutlined,
    FileTextOutlined,
    ScanOutlined,
    UserOutlined
} from '@ant-design/icons';
import {
    CreateUpdateDeviceModal,
    CreateUpdateMemberModal,
    CreateUpdateVehicleModal,
    DeviceTable,
    GuestsTable,
    InvitedMembersTable,
    Loader,
    MembersTable,
    SuspendModal,
    VehicleTable,
    VisitorsTable
} from '../index';
import OccupantVehicleService from '../../services/occupant-vehicle.service';
import {
    createVehiclePlate,
    createVehiclePlateForRealtimeDatabase,
    isPlateUnique
} from '../../utils/helpers/vehicle.helper';
import { PROPERTY_TAB } from '../../utils/constants/property';
import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import {
    getPropertyInvitedMembers,
    makeGetOccupantMembers,
    makeGetPropertyGuests,
    makeGetTenantMembers
} from '../../store/selectors/Property';
import { openCompactModal, openModal } from '../../utils/modal/modal.helper';
import {
    addingNewMember,
    checkingExistingMember,
    removingMember,
    removingPending,
    suspendingPropertyGuest,
    transferMember,
    updatingMember
} from '../../store/actions/Members';
import { getPropertyByIdCollection } from '../../store/actions/Properties';
import {
    getPropertyMembers,
    resetPropertyMembers
} from '../../store/actions/VirtualGuard';
import DeleteModal from '../DeleteModal';
import { getOrgVehiclePlates, getPropertyVehicles } from '../../store/selectors/Vehicle';
import ConfirmActionModal from '../ConfirmActionModal';
import TransferMemberModal from './TransferMemberModal';
import PropertyVisitorService from '../../services/property-visitor.service';
import CreateUpdateNoteModal from './Notes/CreateUpdateNoteModal';
import NoteTable from './Notes/NotesTable';
import { openSnackbar } from 'store/actions/Snackbar';
import { usePermissions } from 'hooks/usePermissions';
import { OrgPermissions, moduleKeys } from 'utils/permissions';
import CreateUpdateTenantModal from './CreateUpdateTenantModal';
import TenantsTable from 'components/Members/TenantsTable';
import { propertyTypes, userRoles, userTypes } from '../../utils/constants';
import OrgPropertyVisitorService from 'services/org-property-visitor.service';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import capitalize from 'utils/capitalize';
import ConfirmModal, { ConfirmModalType } from 'components/common/ConfirmModal';
import OrgPropertiesService from 'services/org-properties.service';
import InvitesService from 'services/invites.service';
import ConvertUserModal from './ConvertUserModal';
import { normalizeOwners } from 'utils/devices/normalizeOwners';
import { getDeviceAssignEligibility } from 'utils/devices/deviceAssignEligibility';
import ManagersTable from '../Members/ManagersTable';
import SetUpdateRentalModal from './SetUpdateRentalModal';
import RentalService from '../../services/rental.service';
import FullscreenLoader from '../common/FullscreenLoader';
import DeviceTypesService from 'services/device-types.service';
import DeleteVerificationModal from '../common/DeleteVerificationModal';

export const processDeviceData = devices =>
    devices.map(
        ({
            access_groups,
            created_at,
            device_number,
            device_type,
            expires_at,
            activate_at,
            id,
            group_name,
            facility_code,
            owner,
            disabled,
            status,
            vehicle,
            has_ca4k_device,
            org_id,
            note,
            // Consumer-issued devices reach these tabs too (they sit on the org's own property), and
            // the edit modal locks owner/property/vehicle off this field — dropping it here left
            // those fields editable against a backend that rejects the change.
            sharing
        }) => {
            return {
                access_groups,
                created_at,
                has_ca4k_device,
                device_number,
                device_type,
                expires_at,
                activate_at,
                id,
                group_name,
                facility_code,
                owner,
                owner_name: `${owner.first_name} ${owner.last_name}`,
                disabled,
                status,
                vehicle,
                org_id,
                note,
                sharing
            };
        }
    );

// Per-tab "filter bar open?" state shared by PropertyTabPanel and
// NonMemberPropertyTabPanel, which both build a TABS object keyed by
// PROPERTY_TAB and need the same {active, onClick} shape for TabPanel's
// filterToggle prop.
export const useFilterToggle = () => {
    const [openTabs, setOpenTabs] = useState({});
    const buildFilterToggle = tabKey => ({
        active: !!openTabs[tabKey],
        onClick: () => setOpenTabs(prev => ({ ...prev, [tabKey]: !prev[tabKey] }))
    });
    return { buildFilterToggle };
};

const PropertyTabPanel = ({
    activeOrg,
    property,
    orgProperties,
    members,
    membersFound,
    memberSuccess,
    getPropertyByIdCollection,
    getActiveOrg,
    getPropertyMembers,
    resetPropertyMembers,
    checkingExistingMember,
    addingNewMember,
    updatingMember,
    transferMember,
    removingMember,
    removingPending,
    suspendingPropertyGuest,
    propertyVehicles,
    orgVehiclePlates,
    devices,
    showResetPropertyModal,
    setShowResetPropertyModal,
    rentalManagersRefreshKey,
    openSnackbar,
    modals,
    membersLoading,
    loading,
    userData
}) => {
    const { id: propertyId } = useParams();
    const dispatch = useDispatch();
    console.log(devices);

    const isSuperAdmin = useMemo(
        () => Array.isArray(userData?.permissions) && userData.permissions.includes('super_admin'),
        [userData]
    );
    const [deleteVerificationMember, setDeleteVerificationMember] = useState(null);

    const [deviceTypes, setDeviceTypes] = useState({});
    useEffect(() => {
        let cancelled = false;
        DeviceTypesService.getDeviceTypes()
            .then(data => {
                if (!cancelled) setDeviceTypes(data || {});
            })
            .catch(err => {
                if (!cancelled) {
                    SnackbarNotify.error(
                        `Failed to load device types: ${
                            err?.message || 'Unexpected error'
                        }`
                    );
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const { hasPermission } = usePermissions();
    const canManageOccupants = hasPermission(OrgPermissions.MANAGE_OCCUPANTS);

    const { buildFilterToggle } = useFilterToggle();
    const occupantsFilterToggle = buildFilterToggle(PROPERTY_TAB.OCCUPANTS);
    const vehiclesFilterToggle = buildFilterToggle(PROPERTY_TAB.VEHICLES);
    const devicesFilterToggle = buildFilterToggle(PROPERTY_TAB.DEVICES);

    const deviceData = useMemo(() => processDeviceData(devices), [devices]);

    const selectOccupants = useMemo(makeGetOccupantMembers, []);
    const selectTenants = useMemo(makeGetTenantMembers, []);
    const selectGuests = useMemo(makeGetPropertyGuests, []);

    const occupants = useSelector(state => selectOccupants(state, propertyId));
    const tenants = useSelector(state => selectTenants(state, propertyId));
    const guests = useSelector(state => selectGuests(state, propertyId));

    const invitedMembers = useSelector(getPropertyInvitedMembers);

    const [visitorsLoading, setVisitorsLoading] = useState(false);
    const [visitors, setVisitors] = useState(null);
    const [visitorLoading, setVisitorLoading] = useState(false);

    const [inviteConfirmOpen, setInviteConfirmOpen] = useState(false);
    const [inviteSelectedMember, setInviteSelectedMember] = useState(null);
    const [convertingInvite, setConvertingInvite] = useState(false);
    const [resettingProperty, setResettingProperty] = useState(false);
    const [transferring, setTransferring] = useState(false);
    const [removingPropertyManager, setRemovingPropertyManager] = useState(false);

    const isShortTermRental =
        property?.type === propertyTypes.RENTAL &&
        property?.rental_start === null &&
        property?.rental_end === null;

    const fetchVisitors = useCallback(async () => {
        if (!propertyId) return;

        setVisitorsLoading(true);
        try {
            const response = await PropertyVisitorService.getVisitors(propertyId);
            if (response) {
                setVisitors(response);
            }
        } catch (error) {
            console.error('Failed to fetch visitors:', error);
        } finally {
            setVisitorsLoading(false);
        }
    }, [propertyId]);

    useEffect(() => {
        fetchVisitors();
    }, [fetchVisitors]);

    useEffect(() => {
        if (propertyId) {
            getPropertyByIdCollection(propertyId);
            getPropertyMembers({ propertyId });
        }
    }, [propertyId, getPropertyByIdCollection, getPropertyMembers, activeOrg]);

    useEffect(() => {
        return () => {
            resetPropertyMembers();
        };
    }, [resetPropertyMembers]);

    const refreshPropertyData = () => {
        getPropertyByIdCollection(property.id);
    };

    const handleSuspendVisitor = async visitor => {
        setVisitorLoading(true);
        const isSuspended = visitor.suspended;
        try {
            await OrgPropertyVisitorService.suspendOccupantsVisitor(
                activeOrg.org_id,
                propertyId,
                visitor.id,
                !isSuspended
            );
            SnackbarNotify.success(
                `Visitor ${capitalize(visitor.first_name)} ${capitalize(
                    visitor.last_name
                )} has been ${isSuspended ? 'unsuspended' : 'suspended'}.`
            );
        } catch (error) {
            console.error('Error adding visitor:', error);
            SnackbarNotify.error(
                `Error ${isSuspended ? 'unsuspending' : 'suspending'} visitor: ${
                    error?.message || 'Unexpected error'
                }`
            );
        } finally {
            setVisitorLoading(false);
        }
        fetchVisitors();
    };

    const handleRemoveVisitor = async visitor => {
        setVisitorLoading(true);
        try {
            await OrgPropertyVisitorService.removeOccupantsVisitor(
                activeOrg.org_id,
                propertyId,
                visitor.id
            );

            SnackbarNotify.success(
                `Visitor ${capitalize(visitor.first_name)} ${capitalize(
                    visitor.last_name
                )} has been deleted.`
            );
        } catch (error) {
            console.error('Error adding visitor:', error);
            SnackbarNotify.error(
                `Error removing visitor: ${error?.message || 'Unexpected error'}`
            );
        } finally {
            setVisitorLoading(false);
        }
        fetchVisitors();
    };

    const handleOpenMemberModal = (
        member = {
            first_name: '',
            last_name: '',
            email: '',
            phone: {
                number: '',
                code: '1',
                country: 'US'
            },
            properties: null
        },
        type = MODAL_ACTION_TYPE.ADD
    ) => {
        openModal(
            CreateUpdateMemberModal,
            {
                selectedMember: member,
                type,
                found: membersFound,
                checkingExistingMember,
                addingNewMember,
                success: memberSuccess,
                property,
                handlePropertyMembersDelete: handleDeletePropertyOccupant,
                orgId: activeOrg.org_id,
                activeOrg,
                updatingMember,
                onTransferMember: () => handleOnTransferMember(member),
                occupants,
                invited: invitedMembers.filter(invite => invite.role === 'resi')
            },
            { fullWidth: true }
        );
    };

    const handleOpenTenantModal = (
        tenant = {
            first_name: '',
            last_name: '',
            email: '',
            phone: {
                number: '',
                code: '1',
                country: 'US'
            },
            properties: null
        },
        type = MODAL_ACTION_TYPE.ADD
    ) => {
        openModal(
            CreateUpdateTenantModal,
            {
                selectedTenant: tenant,
                type,
                property,
                orgId: activeOrg.org_id,
                activeOrg
            },
            { fullWidth: true }
        );
    };

    const handleOpenRentalManagerModal = () => {
        openModal(SetUpdateRentalModal, {
            property,
            activeOrg,
            onUpdate: () => {
                refreshPropertyData();
                fetchRentalManagers();
            },
            onTerminate: () => {
                refreshPropertyData();
                fetchRentalManagers();
            }
        });
    };

    const handleOnTransferMember = member => {
        openModal(TransferMemberModal, {
            member,
            properties: orgProperties,
            onTransfer: selectedProperty => {
                openCompactModal(ConfirmActionModal, {
                    title: 'Confirm occupant transfer',
                    body: `transfer ${member.first_name} ${member.last_name} to address ${selectedProperty.address}`,
                    onConfirm: async () => {
                        try {
                            setTransferring(true);
                            await OrgPropertiesService.transferUser(
                                activeOrg.org_id,
                                property.id,
                                member.uid,
                                {
                                    to_property_id: selectedProperty.id
                                }
                            );
                            dispatch(hideAllModals());
                            refreshPropertyData();
                            await fetchVisitors();
                            SnackbarNotify.success('Member transferred successfully.');
                        } catch (error) {
                            console.error('Error transferring member:', error);
                            SnackbarNotify.error(
                                `Failed to transfer member: ${
                                    error?.message || 'Unexpected error'
                                }`
                            );
                        } finally {
                            setTransferring(false);
                        }
                    },
                    confirmButtonLabel: 'Transfer'
                });
            }
        });
    };

    const handleOpenDeviceModal = (device, type = MODAL_ACTION_TYPE.ADD) => {
        const eligibility = getDeviceAssignEligibility(activeOrg?.modules);
        const deviceObj = device || {
            owner: null,
            facility_code: '',
            device_number: '',
            access_groups: [],
            device_type: '',
            vehicle: null
        };

        openModal(CreateUpdateDeviceModal, {
            selectedDevice: deviceObj,
            type,
            activeOrgId: activeOrg?.org_id,
            activeOrg,
            owners: normalizeOwners({
                members: eligibility.occupants ? [...occupants, ...tenants] : [],
                invitedMembers: eligibility.invitedOccupants
                    ? invitedMembers.filter(m => m.role !== userRoles.GUEST)
                    : [],
                guests: eligibility.propertyGuests ? guests : []
            }),
            propertyId,
            deviceTypes,
            openSnackbar,
            refreshPropertyData
        });
    };

    const handleOpenVehicleModal = (
        vehicle = {
            country: 'USA'
        },
        modalType = MODAL_ACTION_TYPE.ADD
    ) => {
        const orgId = activeOrg.org_id;
        const isEdit = modalType === MODAL_ACTION_TYPE.EDIT;

        const handleSubmit = async (vehiclePayload, onClose) => {
            const vehicleFields = {
                tag: vehiclePayload.tag,
                make: vehiclePayload.make,
                model: vehiclePayload.model,
                state: vehiclePayload.state,
                ...(vehiclePayload.country != null
                    ? { country: vehiclePayload.country }
                    : {}),
                year:
                    vehiclePayload.year != null && vehiclePayload.year !== ''
                        ? String(vehiclePayload.year)
                        : '',
                color:
                    vehiclePayload.color != null && vehiclePayload.color !== ''
                        ? vehiclePayload.color
                        : ''
            };
            const newOwnerId = vehiclePayload.ownerId;
            const ownerChanged = isEdit && newOwnerId && newOwnerId !== vehicle.ownerId;

            const newPlate = createVehiclePlate(vehicleFields);
            const normalizedNewPlate =
                createVehiclePlateForRealtimeDatabase(vehicleFields);
            const plateChanged = !isEdit || createVehiclePlate(vehicle) !== newPlate;

            if (!isPlateUnique(normalizedNewPlate, orgVehiclePlates) && plateChanged) {
                openSnackbar({
                    open: true,
                    message: `Vehicle plate "${newPlate}" already exists in the organization`,
                    variant: 'alert',
                    alert: { color: 'error' }
                });
                return;
            }

            try {
                if (isEdit && !ownerChanged) {
                    await OccupantVehicleService.updateVehicle(
                        orgId,
                        vehicle.ownerId,
                        vehicle.tag,
                        vehicleFields
                    );
                } else if (ownerChanged) {
                    // Owner changed → move the vehicle. Add it to the NEW owner FIRST, then remove
                    // it from the OLD owner, so a failed add never leaves the vehicle orphaned off
                    // both members. A 409 means the vehicle already exists on the new owner → the
                    // move is effectively done, so fall through and detach it from the old owner;
                    // any other error aborts the move with the vehicle still safely on the old owner.
                    try {
                        await OccupantVehicleService.addVehicle(
                            orgId,
                            newOwnerId,
                            vehicleFields
                        );
                    } catch (addError) {
                        if (addError?.statusCode !== 409) throw addError;
                    }
                    await OccupantVehicleService.deleteVehicle(
                        orgId,
                        vehicle.ownerId,
                        vehicle.tag
                    );
                } else {
                    await OccupantVehicleService.addVehicle(
                        orgId,
                        newOwnerId,
                        vehicleFields
                    );
                }

                openSnackbar({
                    open: true,
                    message: `Vehicle ${isEdit ? 'updated' : 'added'} successfully`,
                    variant: 'alert',
                    alert: { color: 'success' }
                });
                onClose?.();
            } catch (error) {
                openSnackbar({
                    open: true,
                    message:
                        error?.userMessage ||
                        error?.message ||
                        `Failed to ${isEdit ? 'update' : 'add'} vehicle`,
                    variant: 'alert',
                    alert: { color: 'error' }
                });
            }
        };

        const handleDelete = (_vehicle, closeVehicleModal) => {
            openCompactModal(DeleteModal, {
                title: `${vehicle.tag}`,
                body: 'Delete Vehicle',
                onDelete: async () => {
                    try {
                        await OccupantVehicleService.deleteVehicle(
                            orgId,
                            vehicle.ownerId,
                            vehicle.tag
                        );
                        openSnackbar({
                            open: true,
                            message: 'Vehicle deleted successfully',
                            variant: 'alert',
                            alert: { color: 'success' }
                        });
                        closeVehicleModal?.();
                    } catch (error) {
                        openSnackbar({
                            open: true,
                            message:
                                error?.userMessage ||
                                error?.message ||
                                'Failed to delete vehicle',
                            variant: 'alert',
                            alert: { color: 'error' }
                        });
                    }
                }
            });
        };

        openModal(CreateUpdateVehicleModal, {
            vehicle: vehicle,
            users: [...occupants, ...tenants],
            onSubmit: handleSubmit,
            onDelete: handleDelete,
            modalType
        });
    };

    const handleOpenNoteModal = ({ note, type }) => {
        const handleSubmit = async (data, onClose) => {
            try {
                if (type === 'add') {
                    await OrgPropertiesService.addPropertyNote(
                        activeOrg.org_id,
                        property.id,
                        { content: data.content }
                    );

                    SnackbarNotify.success('Note added successfully');
                }

                if (type === 'edit') {
                    await OrgPropertiesService.editPropertyNote(
                        activeOrg.org_id,
                        property.id,
                        note.id,
                        {
                            content: data.content
                        }
                    );

                    SnackbarNotify.success('Note updated successfully');
                }
                onClose?.();
            } catch (error) {
                console.error(error);

                SnackbarNotify.error(
                    error?.message ||
                        `Failed to ${type === 'add' ? 'add' : 'update'} note`
                );
            }
        };

        const handleDelete = async (note, onClose) => {
            try {
                await OrgPropertiesService.removePropertyNote(
                    activeOrg.org_id,
                    property.id,
                    note.id
                );
                onClose?.();
                SnackbarNotify.success('Note deleted successfully');
            } catch (error) {
                console.error(error);
                SnackbarNotify.error('Failed to delete note');
            }
        };

        openModal(CreateUpdateNoteModal, {
            note: note,
            onSubmit: handleSubmit,
            onDelete: handleDelete,
            modalType: type
        });
    };

    const addOccupantAction = canManageOccupants
        ? {
              label: 'Add Occupant',
              icon: <UserOutlined />,
              handler: () => handleOpenMemberModal()
          }
        : null;

    const hasPrimary = occupants?.some(
        occ =>
            occ.properties &&
            Object.values(occ.properties).some(prop => prop.primary === true)
    );

    const addRentalManagerAction = {
        label: 'Add Manager',
        icon: <UserOutlined />,
        handler: () => handleOpenRentalManagerModal(),
        disabled: removingPropertyManager
    };

    const addTenantAction = {
        label: 'Add Tenant',
        icon: <UserOutlined />,
        handler: () => handleOpenTenantModal(),
        disabled: !hasPrimary || isShortTermRental,
        tooltipText: isShortTermRental
            ? 'You cannot add tenants to Short-Term Rental properties.'
            : hasPrimary
            ? ''
            : 'You must first add the Owner to the Property in order to be able to add Tenants'
    };

    const handleDeletePropertyOccupant = (member, onClose) => {
        openCompactModal(DeleteModal, {
            title: `${member.first_name} ${member.last_name}`,
            body: 'Remove Occupant Access',
            onDelete: () => {
                removingMember({
                    member: member,
                    orgId: activeOrg.org_id,
                    propertyId
                });
                onClose();
            }
        });
    };

    const handleDeletePropertyGuest = member => {
        openModal(DeleteModal, {
            title: `${member.first_name} ${member.last_name}`,
            body: 'Remove Guest Access',
            onDelete: () => {
                removingMember({
                    member: member,
                    orgId: activeOrg.org_id,
                    propertyId
                });
            }
        });
    };

    const handleDeleteInvitedMember = member => {
        const inviteOrgId = member?.org_id || activeOrg?.org_id;
        const invitePropertyId = member?.property_id || propertyId;
        const inviteId = member?.invite_id || member?.id;

        openModal(DeleteModal, {
            title: `${member.first_name} ${member.last_name}`,
            body: 'Remove Invitation',
            onDelete: async () => {
                try {
                    await InvitesService.deleteInvite(
                        inviteOrgId,
                        invitePropertyId,
                        inviteId
                    );
                    SnackbarNotify.success('Invitation removed.');
                } catch (error) {
                    console.error('Error removing invitation:', error);
                    SnackbarNotify.error(
                        `Error removing invitation: ${
                            error?.message || 'Unexpected error'
                        }`
                    );
                }
            }
        });
    };

    const handleApproveInvitedMember = async member => {
        const inviteOrgId = member?.org_id || activeOrg?.org_id;
        const invitePropertyId = member?.property_id || propertyId;
        const inviteId = member?.invite_id || member?.id;

        try {
            await InvitesService.approveInvite(inviteOrgId, invitePropertyId, inviteId);
            SnackbarNotify.success(
                `${capitalize(member?.first_name || '')} ${capitalize(
                    member?.last_name || ''
                )} — invite sent.`.trim()
            );
        } catch (error) {
            console.error('Error approving invitation:', error);
            SnackbarNotify.error(
                `Error approving invitation: ${error?.message || 'Unexpected error'}`
            );
        }
    };

    const handleConvertInvitedMember = member => {
        if (!member) return;
        setInviteSelectedMember(member);
        setInviteConfirmOpen(true);
    };

    const handleCloseInviteConfirm = () => {
        if (convertingInvite) return;
        setInviteConfirmOpen(false);
        setInviteSelectedMember(null);
    };

    const handleRemoveManager = async manager => {
        setRemovingPropertyManager(true);
        try {
            const managerId =
                manager.status === 'invited'
                    ? manager.invite_id
                    : manager.id || manager.uid;
            await RentalService.removeRentalManagerFromProperty(
                activeOrg.org_id,
                propertyId,
                managerId
            );
            SnackbarNotify.success('Manager removed successfully.');
            fetchRentalManagers();
        } catch (error) {
            console.error(error);
            SnackbarNotify.error(error?.message || 'Failed to remove manager');
        } finally {
            setRemovingPropertyManager(false);
        }
    };

    const handleConfirmInviteConvert = async () => {
        if (!inviteSelectedMember || convertingInvite) return;

        try {
            setConvertingInvite(true);

            await OrgPropertiesService.convertInvite(
                inviteSelectedMember.org_id,
                inviteSelectedMember.property_id,
                inviteSelectedMember.invite_id,
                {
                    role: userRoles.TENANT,
                    type: userTypes.LONG_TERM,
                    primary: false,
                    access_begins: null,
                    access_expires: null,
                    permissions: {
                        calls: true,
                        invite: true
                    }
                }
            );

            SnackbarNotify.success(
                `${capitalize(inviteSelectedMember.first_name)} ${capitalize(
                    inviteSelectedMember.last_name
                )} invite has been converted to a tenant invite.`
            );

            handleCloseInviteConfirm();
            refreshPropertyData();
        } catch (error) {
            console.error('Error converting invite:', error);
            SnackbarNotify.error(
                `Error converting invite: ${error?.message || 'Unexpected error'}`
            );
        } finally {
            setConvertingInvite(false);
        }
    };

    const handleConvertUser = (member, toRole, toType) => {
        if (!member) return;

        const fromRole =
            member?.properties?.[propertyId]?.role || member?.role || 'Guest';

        const guestsEnabled = !!activeOrg?.modules?.includes(moduleKeys.GUESTS);

        openModal(
            ConvertUserModal,
            {
                member,
                propertyId,
                fromRole,
                toRole,
                activeOrg,
                onConfirm: async values => {
                    try {
                        await OrgPropertiesService.convertUser(
                            activeOrg.org_id,
                            propertyId,
                            member.uid,
                            {
                                role: toRole,
                                type: toType,
                                primary: !!values.primary,
                                minor: !!values.minor,
                                permissions: {
                                    calls: !values.hideFromCalls,
                                    invite: guestsEnabled && !values.disableGuestInvites
                                }
                            }
                        );

                        SnackbarNotify.success(
                            `${capitalize(member.first_name)} ${capitalize(
                                member.last_name
                            )} converted to ${toRole}.`
                        );

                        refreshPropertyData();
                    } catch (error) {
                        console.error('Error converting user:', error);
                        SnackbarNotify.error(
                            `Error converting user: ${
                                error?.message || 'Unexpected error'
                            }`
                        );
                    }
                }
            },
            { fullWidth: true }
        );
    };

    const resetProperty = useCallback(() => {
        if (!property) return;

        const addressLine = `${property.address_1 ?? ''}${
            property.address_2 ? ` ${property.address_2}` : ''
        }`.trim();

        openModal(DeleteModal, {
            title: addressLine,
            body: 'Clear Property Data',
            custom: {
                prefix: 'Do you really want to reset ',
                postfix:
                    '? All Occupants, Guests, Invitations, Devices, Vehicles & Notes will be permanently removed. This action cannot be undone.'
            },
            onDelete: async () => {
                try {
                    setResettingProperty(true);

                    await OrgPropertiesService.clearPropertyData(
                        activeOrg.org_id,
                        property.id
                    );
                    setVisitors(null);
                    SnackbarNotify.success('Property data was reset successfully.');
                } catch (error) {
                    console.error('Error clearing property data:', error);
                    SnackbarNotify.error(
                        `Failed: Property data was not reset successfully. ${
                            error?.message || 'Unexpected error'
                        }`
                    );
                } finally {
                    setResettingProperty(false);
                }
            }
        });
    }, [property, activeOrg?.org_id]);

    useEffect(() => {
        if (showResetPropertyModal && modals && modals.length === 0) {
            setShowResetPropertyModal(false);
            resetProperty();
        }
    }, [showResetPropertyModal, setShowResetPropertyModal, resetProperty, modals]);

    const [rentalManagers, setRentalManagers] = useState([]);
    const [rentalManagersLoading, setRentalManagersLoading] = useState(false);

    const fetchRentalManagers = useCallback(async () => {
        if (!activeOrg?.org_id) return;
        setRentalManagersLoading(true);
        try {
            const all = await RentalService.getOrgRentalManagers(activeOrg.org_id);
            setRentalManagers(
                all.filter(m => m.managed_property_ids?.includes(propertyId))
            );
        } catch (err) {
            console.error('Failed to fetch rental managers', err);
        } finally {
            setRentalManagersLoading(false);
        }
    }, [activeOrg?.org_id, propertyId]);

    useEffect(() => {
        fetchRentalManagers();
    }, [fetchRentalManagers, rentalManagersRefreshKey]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const TABS = {
        [PROPERTY_TAB.OCCUPANTS]: {
            label: 'Occupants',
            count: occupants?.length || 0,
            action: addOccupantAction,
            filterToggle: occupantsFilterToggle,
            content: (
                <MembersTable
                    members={occupants}
                    canManage={canManageOccupants}
                    showFilters={occupantsFilterToggle.active}
                    isRental={property?.type === propertyTypes.RENTAL}
                    onMakeTenant={member =>
                        handleConvertUser(member, userRoles.TENANT, userTypes.LONG_TERM)
                    }
                    onMemberClick={
                        canManageOccupants
                            ? member =>
                                  handleOpenMemberModal(member, MODAL_ACTION_TYPE.EDIT)
                            : null
                    }
                    onDeleteVerification={
                        isSuperAdmin ? member => setDeleteVerificationMember(member) : null
                    }
                />
            )
        },
        [PROPERTY_TAB.TENANTS]: {
            label: 'Tenants',
            count: tenants?.length || 0,
            action: hasPermission(OrgPermissions.MANAGE_OCCUPANTS)
                ? addTenantAction
                : null,
            content: (
                <TenantsTable
                    tenants={tenants}
                    onTenantClick={
                        hasPermission(OrgPermissions.MANAGE_OCCUPANTS)
                            ? tenant =>
                                  handleOpenTenantModal(tenant, MODAL_ACTION_TYPE.EDIT)
                            : null
                    }
                    onDeleteVerification={
                        isSuperAdmin ? member => setDeleteVerificationMember(member) : null
                    }
                />
            )
        },
        [PROPERTY_TAB.GUESTS]: {
            label: 'Guests',
            count: guests?.length || 0,
            content: (
                <GuestsTable
                    members={guests}
                    propertyId={propertyId}
                    isRental={property?.type === propertyTypes.RENTAL}
                    canManage={canManageOccupants}
                    onMakeGuestOccupant={member =>
                        handleConvertUser(member, userRoles.RESIDENT, userTypes.MEMBER)
                    }
                    onMakeGuestTenant={member =>
                        handleConvertUser(member, userRoles.TENANT, userTypes.LONG_TERM)
                    }
                    onGuestSuspend={member => {
                        openModal(SuspendModal, {
                            title: `${member?.first_name} ${member?.last_name}`,
                            body: `Are you sure you want to ${
                                member?.properties[propertyId]?.suspended
                                    ? 'unsuspend'
                                    : 'suspend'
                            } this guest?`,
                            suspended: member?.properties[propertyId]?.suspended,
                            onSuspend: () => {
                                suspendingPropertyGuest({
                                    member: {
                                        ...member,
                                        suspended:
                                            !member?.properties[propertyId]?.suspended
                                    },
                                    orgId: activeOrg.org_id,
                                    propertyId: property.id
                                });
                            }
                        });
                    }}
                    onGuestDelete={member => handleDeletePropertyGuest(member)}
                    onDeleteVerification={
                        isSuperAdmin ? member => setDeleteVerificationMember(member) : null
                    }
                />
            )
        },
        [PROPERTY_TAB.VISITORS]: {
            label: 'Visitors',
            count: visitors?.length || 0,
            content: (
                <VisitorsTable
                    visitors={visitors || []}
                    onRemove={handleRemoveVisitor}
                    onSuspend={handleSuspendVisitor}
                    canManage={canManageOccupants}
                />
            )
        },
        [PROPERTY_TAB.INVITED]: {
            label: 'Invited',
            count: invitedMembers?.length || 0,
            action: addOccupantAction,
            content: (
                <InvitedMembersTable
                    members={invitedMembers}
                    onInvitedMemberDelete={
                        canManageOccupants
                            ? member => handleDeleteInvitedMember(member)
                            : null
                    }
                    onInvitedMemberApprove={
                        canManageOccupants
                            ? member => handleApproveInvitedMember(member)
                            : null
                    }
                    onInvitedMemberConvert={
                        canManageOccupants
                            ? member => handleConvertInvitedMember(member)
                            : null
                    }
                />
            )
        },
        [PROPERTY_TAB.RENTAL_MANAGERS]: {
            label: 'Rental Managers',
            count: rentalManagers?.length || 0,
            content: (
                <ManagersTable
                    loading={removingPropertyManager}
                    members={rentalManagers}
                    canManage={canManageOccupants}
                    onRemoveManager={handleRemoveManager}
                />
            ),
            action: addRentalManagerAction
        },
        [PROPERTY_TAB.VEHICLES]: {
            label: 'Vehicles',
            count: propertyVehicles?.length || 0,
            action: hasPermission(OrgPermissions.MANAGE_OCCUPANT_VEHICLES)
                ? {
                      label: 'Add Vehicle',
                      icon: <CarOutlined />,
                      handler: () => handleOpenVehicleModal()
                  }
                : null,
            filterToggle: vehiclesFilterToggle,
            content: (
                <VehicleTable
                    vehicles={propertyVehicles}
                    members={members}
                    showFilters={vehiclesFilterToggle.active}
                    onVehicleClick={
                        hasPermission(OrgPermissions.MANAGE_OCCUPANT_VEHICLES)
                            ? vehicle =>
                                  handleOpenVehicleModal(vehicle, MODAL_ACTION_TYPE.EDIT)
                            : null
                    }
                />
            )
        },
        [PROPERTY_TAB.DEVICES]: {
            label: 'Devices',
            count: devices?.length || 0,
            action: hasPermission(OrgPermissions.MANAGE_OCCUPANT_DEVICES)
                ? {
                      label: 'Add Device',
                      icon: <ScanOutlined />,
                      handler: () => handleOpenDeviceModal()
                  }
                : null,
            filterToggle: devicesFilterToggle,
            content: (
                <DeviceTable
                    data={deviceData}
                    showFilters={devicesFilterToggle.active}
                    onDeviceClick={
                        hasPermission(OrgPermissions.MANAGE_OCCUPANT_DEVICES)
                            ? device =>
                                  handleOpenDeviceModal(device, MODAL_ACTION_TYPE.EDIT)
                            : null
                    }
                    deviceTypes={deviceTypes}
                />
            )
        },
        [PROPERTY_TAB.NOTES]: {
            label: 'Notes',
            count: property?.notes && property?.notes.length ? property?.notes.length : 0,
            action: {
                label: 'Add Note',
                icon: <FileTextOutlined />,
                handler: () => handleOpenNoteModal({ type: MODAL_ACTION_TYPE.ADD })
            },
            content: (
                <NoteTable
                    notes={
                        property?.notes && property?.notes.length ? property.notes : []
                    }
                    onNoteClick={note =>
                        handleOpenNoteModal({ note, type: MODAL_ACTION_TYPE.EDIT })
                    }
                />
            )
        }
    };

    const enabledTabs = useMemo(() => {
        const allowedTabs = { ...TABS };
        const hasDeviceModule = activeOrg?.modules?.includes('devices');
        const hasGuestsModule = activeOrg?.modules?.includes(moduleKeys.GUESTS);

        if (!hasDeviceModule) {
            delete allowedTabs[PROPERTY_TAB.DEVICES];
        }

        if (!hasGuestsModule) {
            delete allowedTabs[PROPERTY_TAB.GUESTS];
        }

        if (property && !(property?.type === propertyTypes.RENTAL)) {
            delete allowedTabs[PROPERTY_TAB.TENANTS];
        }

        if (!isShortTermRental) {
            delete allowedTabs[PROPERTY_TAB.RENTAL_MANAGERS];
        }

        return allowedTabs;
    }, [activeOrg, TABS, property, isShortTermRental]);

    if (!enabledTabs) {
        return <Loader />;
    }

    return (
        <>
            {visitorLoading && <Loader />}
            {convertingInvite && <Loader />}
            {removingPropertyManager && <Loader />}
            <FullscreenLoader open={transferring} />

            <TabPanel
                tabs={enabledTabs}
                defaultTab={PROPERTY_TAB.OCCUPANTS}
                actionComponent={ActionButton}
                actionComponentActions={activeTab => TABS[activeTab].action}
                filterToggle={activeTab => TABS[activeTab]?.filterToggle}
            />

            {(visitorsLoading ||
                membersLoading ||
                loading ||
                resettingProperty ||
                rentalManagersLoading ||
                removingPropertyManager) && <Loader />}

            <DeleteVerificationModal
                open={Boolean(deleteVerificationMember)}
                member={deleteVerificationMember}
                onClose={() => setDeleteVerificationMember(null)}
                onDeleted={() => setDeleteVerificationMember(null)}
            />

            <ConfirmModal
                open={inviteConfirmOpen}
                onClose={handleCloseInviteConfirm}
                onConfirm={handleConfirmInviteConvert}
                type={ConfirmModalType.WARNING}
                title="Convert invite to tenant?"
                text={
                    <>
                        This will convert{' '}
                        <b>
                            {`${capitalize(inviteSelectedMember?.first_name) || ''} ${
                                capitalize(inviteSelectedMember?.last_name) || ''
                            }`.trim() || 'this guest'}
                        </b>{' '}
                        to a tenant invite.
                    </>
                }
                confirmText={convertingInvite ? 'Converting...' : 'Confirm'}
                cancelText="Cancel"
            />
        </>
    );
};

const mapStateToProps = ({
    properties,
    vg,
    members,
    user,
    org,
    device,
    manager,
    modal
}) => {
    const { property, loading, error } = properties;
    const { activeOrg, userData } = user;
    const { devices } = device;
    const {
        loading: membersLoading,
        error: membersError,
        member,
        found: membersFound,
        success: memberSuccess
    } = members;

    const orgProperties = properties.orgProperties
        ? Object.values(properties.orgProperties)
        : [];

    const propertyVehicles = getPropertyVehicles(vg, activeOrg, property?.id);
    const orgVehiclePlates = getOrgVehiclePlates(manager);

    const { modals } = modal;

    return {
        activeOrg: activeOrg,
        property,
        orgProperties,
        member,
        members: vg.members,
        membersLoading,
        membersError,
        membersFound,
        memberSuccess,
        propertyVehicles,
        orgVehiclePlates,
        devices: devices || [],
        modals,
        loading,
        error,
        userData
    };
};

export default connect(mapStateToProps, {
    getPropertyByIdCollection,
    getPropertyMembers,
    resetPropertyMembers,
    checkingExistingMember,
    addingNewMember,
    updatingMember,
    transferMember,
    removingMember,
    removingPending,
    suspendingPropertyGuest,
    openSnackbar
})(PropertyTabPanel);
